/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
	DataTable,
	DataTablePFSEvent,
	DataTableSortOrderType
} from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { PaginatorPageState } from 'primereact/paginator';
import { Column, ColumnEditorOptions } from 'primereact/column';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';

import Filters from './filters/Filters';
import Pagination from '../common/pagination/Pagination';
import { CountriesSearchResponseModel } from '../../models/countries/CountriesSearchResponseModel';
import { CountriesListingResponseModel } from '../../models/countries/CountriesListingResponseModel';
import { HttpService, IHttpService } from '../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../services/notifierService';
import { nameof } from '../../shared/utility';

const Countries = (): JSX.Element => {
	const [loading, setLoading] = useState<boolean>(false);
	const [first, setFirst] = useState<number>(0);
	const [rows, setRows] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortField, setSortField] = useState<string>('');
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>();
	const [countryCodeFilter, setCountryCodeFilter] = useState<string>('');
	const [countryDescriptionFilter, setCountryDescriptionFilter] =
		useState<string>('');
	const [shouldReload, setShouldReload] = useState<boolean>(false);
	const [countriesSearch, setCountriesSearch] =
		useState<CountriesSearchResponseModel>();
	const [countriesListing, setCountriesListing] = useState<
		CountriesListingResponseModel[]
	>([]);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const fetchCountries = () => {
		setLoading(true);

		let order = '';
		if (sortOrder) {
			order = sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
		}
		httpService
			.get<CountriesSearchResponseModel>(
				`countries?page=${currentPage}&code=${countryCodeFilter}&description=${countryDescriptionFilter}&sortBy=${sortField}&order=${order}`,
				undefined
			)
			.then((countries) => {
				setCountriesSearch(countries);
				setCountriesListing(countries.models);
				setLoading(false);
			})
			.catch((err) => notifierService.error());
	};

	useEffect(() => {
		fetchCountries();
	}, [
		httpService,
		notifierService,
		currentPage,
		sortField,
		sortOrder,
		countryCodeFilter,
		countryDescriptionFilter
	]);

	useEffect(() => {
		if (shouldReload) {
			fetchCountries();
		}
	}, [shouldReload]);

	const handleInputTextChange = useCallback(
		(
			id: number,
			field: keyof CountriesListingResponseModel,
			value: any
		) => {
			const newCountries: CountriesListingResponseModel[] = [
				...countriesListing
			];
			const countryIndex: number = newCountries.findIndex(
				(b) => b.id === id
			);

			if (countryIndex !== -1) {
				const targetItem = newCountries[countryIndex];
				(targetItem as any)[field] = value;
				setCountriesListing(newCountries);
			}
		},
		[countriesListing, setCountriesListing]
	);

	const handleCellEditor = useCallback(
		(
			cellData: ColumnEditorOptions,
			field: keyof CountriesListingResponseModel
		) => {
			const cellValue = (cellData.rowData[field] as string) || '';
			return (
				<InputText
					value={cellValue}
					onChange={(e: ChangeEvent<HTMLInputElement>) => {
						//@ts-ignore
						cellData.editorCallback(e.target.value);
						handleInputTextChange(
							cellData.rowData.id,
							field,
							e.target.value
						);
					}}
				/>
			);
		},
		[handleInputTextChange]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleInputSwitchChange = async (
		e: InputSwitchChangeParams,
		propName: keyof CountriesListingResponseModel
	) => {
		const countryId: number = +e.target.id;
		const countryIndex: number = countriesListing.findIndex(
			(b) => b.id === countryId
		);

		const brand = countriesListing[countryIndex];

		(brand as any)[propName] = e.target.value;

		try {
			await httpService.put(
				`countries/${countryId}/changeActive`,
				undefined,
				undefined,
				false
			);

			let order = '';
			if (sortOrder) {
				order =
					sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
			}

			const brands = await httpService.get<CountriesSearchResponseModel>(
				`countries?page=${currentPage}&code=${countryCodeFilter}&description=${countryDescriptionFilter}&sortBy=${sortField}&order=${order}`,
				undefined
			);

			setCountriesListing(brands.models);
		} catch {
			notifierService.error();
		}
	};

	const onRenderActive = useCallback(
		(cellData: CountriesListingResponseModel) => {
			return (
				<InputSwitch
					id={cellData.id.toString()}
					checked={cellData.active}
					onChange={(e) => handleInputSwitchChange(e, 'active')}
				/>
			);
		},
		[handleInputSwitchChange]
	);

	const handleEditorSubmit = async (e: any) => {
		const { rowData } = e;
		try {
			await httpService.put(
				`countries/${rowData.id}`,
				JSON.stringify(rowData),
				undefined,
				false
			);
		} catch (err) {
			notifierService.error(err);
		}
	};

	const onPageChange = (event: PaginatorPageState) => {
		setFirst(event.first);
		setRows(event.rows);
		setCurrentPage(event.page! + 1);
	};

	const onSort = (event: DataTablePFSEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	return (
		<>
			<Filters
				setCountryCodeFilter={setCountryCodeFilter}
				setCountryDescriptionFilter={setCountryDescriptionFilter}
				setShouldReload={setShouldReload}
			/>

			<DataTable
				value={countriesListing}
				responsiveLayout='scroll'
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				loading={loading}
			>
				<Column
					style={{ width: '10%' }}
					sortable
					header='Status'
					body={onRenderActive}
					field={nameof<CountriesListingResponseModel>('active')}
				/>

				<Column
					style={{ width: '18%' }}
					sortable
					header='Country code'
					field={nameof<CountriesListingResponseModel>('code')}
					editor={(ep) => handleCellEditor(ep, 'code')}
					onCellEditComplete={handleEditorSubmit}
				/>

				<Column
					style={{ width: '30%' }}
					sortable
					header='Country description'
					field={nameof<CountriesListingResponseModel>('description')}
					editor={(ep) => handleCellEditor(ep, 'description')}
					onCellEditComplete={handleEditorSubmit}
				/>
			</DataTable>

			<Pagination
				totalRecords={countriesSearch?.total}
				rows={rows}
				first={first}
				onPageChange={onPageChange}
			/>
		</>
	);
};

export default Countries;
